import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calculator, FileText, Calendar, AlertCircle, CheckCircle2,
  Euro, TrendingDown, Loader2, ChevronRight, Info, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Barèmes impôts 2025 (revenus 2024)
const TAX_BRACKETS_2025 = [
  { min: 0, max: 11294, rate: 0 },
  { min: 11294, max: 28797, rate: 0.11 },
  { min: 28797, max: 82341, rate: 0.30 },
  { min: 82341, max: 177106, rate: 0.41 },
  { min: 177106, max: Infinity, rate: 0.45 }
];

// Calendrier fiscal 2026
const fiscalCalendar = [
  { title: "Ouverture déclaration en ligne", date: "10 avril 2026", status: "upcoming", month: 4 },
  { title: "Date limite Zone 1 (départements 01-19)", date: "22 mai 2026", status: "upcoming", month: 5 },
  { title: "Date limite Zone 2 (départements 20-54)", date: "29 mai 2026", status: "upcoming", month: 5 },
  { title: "Date limite Zone 3 (départements 55-976)", date: "5 juin 2026", status: "upcoming", month: 6 },
  { title: "Réception avis d'imposition", date: "Juillet-Août 2026", status: "upcoming", month: 7 },
  { title: "1er prélèvement (si impôt > 300€)", date: "26 septembre 2026", status: "upcoming", month: 9 },
];

export const ImpotPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const [showSimulator, setShowSimulator] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    revenu_net_imposable: '',
    situation_familiale: 'celibataire',
    nb_parts: 1,
    nb_enfants: 0,
    heures_supplementaires: '',
    dons_associations: '',
    frais_garde: '',
    emploi_domicile: ''
  });

  // Calculate tax
  const calculateTax = () => {
    setLoading(true);
    
    setTimeout(() => {
      const revenu = parseFloat(formData.revenu_net_imposable) || 0;
      const parts = parseFloat(formData.nb_parts) || 1;
      const heuresSup = parseFloat(formData.heures_supplementaires) || 0;
      const dons = parseFloat(formData.dons_associations) || 0;
      const fraisGarde = parseFloat(formData.frais_garde) || 0;
      const emploiDomicile = parseFloat(formData.emploi_domicile) || 0;
      
      // Quotient familial
      const quotientFamilial = revenu / parts;
      
      // Calcul de l'impôt par tranche
      let impotBrut = 0;
      for (const bracket of TAX_BRACKETS_2025) {
        if (quotientFamilial > bracket.min) {
          const taxableInBracket = Math.min(quotientFamilial, bracket.max) - bracket.min;
          impotBrut += taxableInBracket * bracket.rate;
        }
      }
      
      // Multiplier par le nombre de parts
      impotBrut = impotBrut * parts;
      
      // Réductions d'impôts
      const reductionDons = Math.min(dons * 0.66, revenu * 0.20); // 66% plafonné à 20% du revenu
      const reductionGarde = Math.min(fraisGarde * 0.50, 1750 * formData.nb_enfants); // 50% plafonné à 1750€/enfant
      const reductionEmploi = Math.min(emploiDomicile * 0.50, 6000); // 50% plafonné à 6000€
      const exonerationHeuresSup = Math.min(heuresSup, 7500); // Exonération jusqu'à 7500€
      
      const totalReductions = reductionDons + reductionGarde + reductionEmploi;
      const impotNet = Math.max(0, impotBrut - totalReductions);
      
      // Taux marginal
      let tauxMarginal = 0;
      for (const bracket of TAX_BRACKETS_2025) {
        if (quotientFamilial > bracket.min) {
          tauxMarginal = bracket.rate * 100;
        }
      }
      
      // Taux moyen
      const tauxMoyen = revenu > 0 ? (impotNet / revenu * 100) : 0;
      
      setResult({
        revenu_imposable: revenu,
        quotient_familial: quotientFamilial,
        impot_brut: impotBrut,
        reductions: {
          dons: reductionDons,
          garde: reductionGarde,
          emploi: reductionEmploi,
          total: totalReductions
        },
        exoneration_heures_sup: exonerationHeuresSup,
        impot_net: impotNet,
        impot_mensuel: impotNet / 12,
        taux_marginal: tauxMarginal,
        taux_moyen: tauxMoyen,
        parts: parts,
        non_imposable: impotNet === 0
      });
      
      setLoading(false);
    }, 800);
  };

  // Auto-calculate parts based on situation
  useEffect(() => {
    let parts = 1;
    if (formData.situation_familiale === 'marie' || formData.situation_familiale === 'pacse') {
      parts = 2;
    } else if (formData.situation_familiale === 'parent_isole') {
      parts = 1.5;
    }
    // Ajouter les parts enfants
    const nbEnfants = parseInt(formData.nb_enfants) || 0;
    if (nbEnfants >= 1) parts += 0.5;
    if (nbEnfants >= 2) parts += 0.5;
    if (nbEnfants >= 3) parts += (nbEnfants - 2) * 1; // 1 part par enfant à partir du 3ème
    
    setFormData(prev => ({ ...prev, nb_parts: parts }));
  }, [formData.situation_familiale, formData.nb_enfants]);

  const currentMonth = new Date().getMonth() + 1;

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="impot-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/20 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-[#81d8d0]" />
            </div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Simulateur Impôts 2026
            </h1>
          </div>
          <p className={`mb-8 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
            Calcule ton impôt sur le revenu 2025 avec les barèmes officiels
          </p>
        </motion.div>

        {/* Result Banner */}
        {result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className={`p-6 mb-8 border ${
              result.non_imposable
                ? isDark ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-[#81d8d0]/10 border-[#81d8d0]/30' : 'bg-[#81d8d0]/10 border-[#81d8d0]/40'
            }`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className={`text-sm mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Impôt estimé</p>
                  <p className={`text-4xl font-bold ${result.non_imposable ? 'text-green-500' : 'text-[#81d8d0]'}`}>
                    {result.non_imposable ? '0€' : `${Math.round(result.impot_net).toLocaleString('fr-FR')}€`}
                  </p>
                  {result.non_imposable && (
                    <p className="text-green-500 text-sm font-medium mt-1">Non imposable !</p>
                  )}
                </div>
                <div className="text-center">
                  <p className={`text-sm mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Par mois</p>
                  <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {Math.round(result.impot_mensuel).toLocaleString('fr-FR')}€
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-sm mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Taux moyen</p>
                  <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {result.taux_moyen.toFixed(1)}%
                  </p>
                </div>
              </div>
              
              {/* Details */}
              <div className={`mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div>
                  <p className={isDark ? 'text-[#64748B]' : 'text-gray-500'}>Quotient familial</p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {Math.round(result.quotient_familial).toLocaleString('fr-FR')}€
                  </p>
                </div>
                <div>
                  <p className={isDark ? 'text-[#64748B]' : 'text-gray-500'}>Nombre de parts</p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{result.parts}</p>
                </div>
                <div>
                  <p className={isDark ? 'text-[#64748B]' : 'text-gray-500'}>Taux marginal</p>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{result.taux_marginal}%</p>
                </div>
                <div>
                  <p className={isDark ? 'text-[#64748B]' : 'text-gray-500'}>Réductions</p>
                  <p className="font-medium text-green-500">-{Math.round(result.reductions.total).toLocaleString('fr-FR')}€</p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className={`mt-4 pt-4 border-t text-xs ${isDark ? 'border-white/10 text-[#64748B]' : 'border-gray-200 text-gray-500'}`}>
                ⚠️ Estimation basée sur les barèmes 2025. Le montant réel peut varier selon ta situation exacte.
              </div>
            </Card>
          </motion.div>
        )}

        {/* Simulator Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className={`p-6 mb-8 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Euro size={20} className="text-[#81d8d0]" />
              Calcule ton impôt
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Revenu */}
              <div className="md:col-span-2">
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
                  Revenu net imposable annuel (€) *
                </Label>
                <Input
                  type="number"
                  placeholder="Ex: 25000"
                  value={formData.revenu_net_imposable}
                  onChange={(e) => setFormData({ ...formData, revenu_net_imposable: e.target.value })}
                  className={`mt-1 text-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  data-testid="input-revenu"
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                  Tu le trouves sur ta fiche de paie ou avis d'imposition N-1
                </p>
              </div>

              {/* Situation familiale */}
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Situation familiale</Label>
                <Select 
                  value={formData.situation_familiale} 
                  onValueChange={(v) => setFormData({ ...formData, situation_familiale: v })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibataire">Célibataire</SelectItem>
                    <SelectItem value="marie">Marié(e) / Pacsé(e)</SelectItem>
                    <SelectItem value="pacse">Pacsé(e)</SelectItem>
                    <SelectItem value="divorce">Divorcé(e)</SelectItem>
                    <SelectItem value="veuf">Veuf(ve)</SelectItem>
                    <SelectItem value="parent_isole">Parent isolé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Enfants */}
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Nombre d'enfants à charge</Label>
                <Select 
                  value={String(formData.nb_enfants)} 
                  onValueChange={(v) => setFormData({ ...formData, nb_enfants: parseInt(v) })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4, 5].map(n => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Parts (auto-calculated, read-only) */}
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
                  Nombre de parts fiscales
                  <HelpCircle size={14} className="inline ml-1 text-[#64748B]" />
                </Label>
                <Input
                  type="text"
                  value={formData.nb_parts}
                  readOnly
                  className={`mt-1 ${isDark ? 'bg-white/10 border-white/10 text-[#81d8d0]' : 'bg-gray-100 border-gray-200 text-[#81d8d0]'} font-semibold`}
                />
              </div>

              {/* Heures sup */}
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Heures supplémentaires (€/an)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.heures_supplementaires}
                  onChange={(e) => setFormData({ ...formData, heures_supplementaires: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                  Exonérées jusqu'à 7 500€
                </p>
              </div>
            </div>

            {/* Réductions d'impôts */}
            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`font-medium mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <TrendingDown size={18} className="text-green-500" />
                Réductions d'impôts (optionnel)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Dons associations (€)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.dons_associations}
                    onChange={(e) => setFormData({ ...formData, dons_associations: e.target.value })}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  />
                  <p className={`text-xs mt-1 text-green-500`}>66% déductible</p>
                </div>
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Frais de garde (€)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.frais_garde}
                    onChange={(e) => setFormData({ ...formData, frais_garde: e.target.value })}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  />
                  <p className={`text-xs mt-1 text-green-500`}>50% déductible</p>
                </div>
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Emploi à domicile (€)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.emploi_domicile}
                    onChange={(e) => setFormData({ ...formData, emploi_domicile: e.target.value })}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  />
                  <p className={`text-xs mt-1 text-green-500`}>50% déductible</p>
                </div>
              </div>
            </div>

            <Button
              onClick={calculateTax}
              disabled={loading || !formData.revenu_net_imposable}
              className="w-full mt-6 bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold py-6"
              data-testid="calculate-tax-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Calculator size={20} className="mr-2" />
                  Calculer mon impôt
                </>
              )}
            </Button>
          </Card>
        </motion.div>

        {/* Fiscal Calendar */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Calendrier fiscal 2026
          </h2>
          <div className="space-y-3">
            {fiscalCalendar.map((event, index) => {
              const isPast = event.month < currentMonth;
              const isCurrent = event.month === currentMonth;
              
              return (
                <Card 
                  key={index}
                  className={`p-4 border transition-all ${
                    isCurrent
                      ? isDark ? 'bg-[#81d8d0]/10 border-[#81d8d0]/40' : 'bg-[#81d8d0]/10 border-[#81d8d0]/50'
                      : isPast
                        ? isDark ? 'bg-white/5 border-white/10 opacity-60' : 'bg-gray-50 border-gray-200 opacity-60'
                        : isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isPast 
                        ? 'bg-green-500/20' 
                        : isCurrent 
                          ? 'bg-[#81d8d0]/20' 
                          : isDark ? 'bg-white/10' : 'bg-gray-100'
                    }`}>
                      {isPast ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Calendar className={`w-5 h-5 ${isCurrent ? 'text-[#81d8d0]' : isDark ? 'text-[#94A3B8]' : 'text-gray-400'}`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{event.title}</h4>
                      <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>{event.date}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      isPast
                        ? 'bg-green-500/20 text-green-500'
                        : isCurrent
                          ? 'bg-[#81d8d0]/20 text-[#81d8d0]'
                          : isDark ? 'bg-white/10 text-[#94A3B8]' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isPast ? 'Passé' : isCurrent ? 'Ce mois' : 'À venir'}
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        </motion.div>

        {/* Tax brackets info */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8">
          <Card className={`p-5 border ${isDark ? 'bg-blue-500/5 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Barèmes 2025</h4>
                <div className={`text-sm space-y-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                  <p>• 0% jusqu'à 11 294€</p>
                  <p>• 11% de 11 294€ à 28 797€</p>
                  <p>• 30% de 28 797€ à 82 341€</p>
                  <p>• 41% de 82 341€ à 177 106€</p>
                  <p>• 45% au-delà de 177 106€</p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ImpotPage;
